// Live order tracking — Direction A S4. Polls the public track window
// (the order id is the capability) every four seconds; the timeline grows
// with the order. The lines come through the customer's own door when the
// order was placed from this device. A public order stream (SSE) is the
// upgrade — the API has no public stream yet.

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { OrderLines } from '../components/OrderLines';
import { fetchMyOrder, fetchTrack, money, timeOf, type Order, type TrackView } from '../lib/api';

type Node = { key: string; title: string; detail: string; state: 'done' | 'now' | 'dim' };

function timeline(t: TrackView): Node[] {
  const paid = t.paymentState === 'paid' || t.paymentState === 'partially_refunded';
  const nodes: Node[] = [
    { key: 'placed', title: 'Order placed', detail: `${timeOf(t.placedAt)}${paid ? ` — paid ${money(t.grandTotalInCents)}` : ` — ${money(t.grandTotalInCents)} to pay at the store`}`, state: 'done' },
  ];
  if (t.status === 'rejected' || t.status === 'cancelled') {
    nodes.push({ key: 'end', title: t.status === 'rejected' ? 'The store couldn’t take this order' : 'Order cancelled', detail: t.statusReason ?? `Call the store on ${t.storePhoneE164}`, state: 'now' });
    return nodes;
  }
  const accepted = t.status !== 'new';
  const eta = t.etaMinutes ? timeOf(new Date(new Date(t.placedAt).getTime() + t.etaMinutes * 60_000).toISOString()) : null;
  nodes.push({
    key: 'confirmed',
    title: accepted ? 'Confirmed by the restaurant' : 'Waiting for the restaurant',
    detail: accepted ? (eta ? `Ready around ${eta}` : 'The kitchen has it') : 'Usually under a couple of minutes',
    state: t.status === 'new' ? 'now' : 'done',
  });
  if (t.collectionType === 'delivery') {
    nodes.push({
      key: 'courier',
      title: t.courierStatus ? `Courier: ${t.courierStatus.replace(/_/g, ' ')}` : 'Out for delivery',
      detail: t.trackingUrl ? 'Live courier tracking available' : 'We’ll update this live',
      state: t.status === 'completed' ? 'done' : t.courierStatus ? 'now' : 'dim',
    });
  }
  nodes.push({
    key: 'done',
    title: t.collectionType === 'pickup' ? 'Ready for pickup' : t.collectionType === 'delivery' ? 'Delivered' : 'Served to your table',
    detail: t.status === 'completed' ? 'Enjoy!' : 'We’ll update this live',
    state: t.status === 'completed' ? 'now' : 'dim',
  });
  return nodes;
}

export function Track() {
  const { rid, sid, orderId } = useParams() as { rid: string; sid: string; orderId: string };
  const [sp] = useSearchParams();
  const [t, setT] = useState<TrackView | null>(null);
  const [mine, setMine] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      fetchTrack(rid, sid, orderId).then(
        (v) => { if (live) { setT(v); setError(null); document.title = `Order ${v.docketLabel ?? ''} — ${v.storeName}`; } },
        (e) => live && setError(e instanceof Error ? e.message : 'Could not load the order.'),
      );
    void load();
    fetchMyOrder(rid, orderId).then((o) => live && setMine(o), () => { /* placed elsewhere, or no identity — the window still works */ });
    const iv = setInterval(load, 4000);
    return () => { live = false; clearInterval(iv); };
  }, [rid, sid, orderId]);

  if (error) return <div className="errbox" role="alert">{error}</div>;
  if (!t) return <div className="loading">Finding your order…</div>;

  const eta = t.etaMinutes ? timeOf(new Date(new Date(t.placedAt).getTime() + t.etaMinutes * 60_000).toISOString()) : null;
  const headline =
    t.status === 'new' ? 'Order placed' :
    t.status === 'accepted' || t.status === 'in_progress' ? (eta ? `Confirmed — ${eta}` : 'Confirmed') :
    t.status === 'completed' ? 'All done' :
    t.status === 'rejected' ? 'Order not accepted' : 'Order cancelled';

  return (
    <div className="tr-page">
      <div className="tr-hero" data-status={t.status} data-payment-state={t.paymentState}>
        <div className={'tr-ring' + (t.status === 'completed' ? ' done' : '')} />
        <div className="big">{headline}</div>
        <div className="sub">{t.storeName}{t.docketLabel ? ` · Docket ${t.docketLabel}` : ''}</div>
        {sp.get('paynote') && t.paymentState === 'unpaid' && <div className="tr-note">This store takes payment at the counter.</div>}
      </div>

      <div className="tl" data-section="timeline">
        {timeline(t).map((n) => (
          <div className={`tl-row ${n.state}`} key={n.key} data-step={n.key}>
            <div className="tl-dot" />
            <div>
              <div className="tt">{n.title}</div>
              <div className="td">{n.detail}</div>
            </div>
          </div>
        ))}
      </div>

      {mine && mine.lines.length > 0 && (
        <div className="tr-card" data-section="lines">
          <div className="ct" style={{ marginBottom: 6 }}>Your order</div>
          <OrderLines lines={mine.lines} />
          <div className="co-totals">
            <div className="tr grand"><span>{t.paymentState === 'paid' ? 'Paid' : 'Total'}</span><span data-total>{money(t.grandTotalInCents)}</span></div>
          </div>
        </div>
      )}

      <div className="tr-card">
        <div className="ct">
          {t.collectionType === 'pickup' ? 'Pickup' : t.collectionType === 'delivery' ? 'Delivery' : 'Dine-in'} · {t.paymentState === 'paid' ? `paid ${money(t.grandTotalInCents)}` : `${money(t.grandTotalInCents)} ${t.paymentState.replace(/_/g, ' ')}`}
        </div>
        {t.trackingUrl && <a href={t.trackingUrl} target="_blank" rel="noreferrer" className="linky">Courier tracking ↗</a>}
      </div>

      <div className="tr-card center">
        <div style={{ fontWeight: 700, fontSize: 14 }}>Need to change something?</div>
        <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
          Call the store · <a className="linky" href={`tel:${t.storePhoneE164}`}>{t.storePhoneE164}</a>
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: '18px 0 60px' }}>
        <Link className="linky" to={`/r/${rid}/s/${sid}`}>← Back to the menu</Link>
        {' · '}
        <Link className="linky" to={`/r/${rid}/s/${sid}/orders`}>My orders</Link>
      </div>
    </div>
  );
}
